import { useEffect, useRef, useState } from 'react';
import { twitch, voteOf } from './twitch';

export interface Tally {
  counts: number[]; // per option
  total: number;
  last: string | null; // name of the latest voter
}

/// Counts chat votes for options 1..[options] while [active]. One vote per
/// viewer; changing your mind moves your vote.
export function useChatVote(active: boolean, options: number, onVote?: () => void): Tally {
  const byUser = useRef(new Map<string, number>());
  const [tally, setTally] = useState<Tally>({ counts: new Array(options).fill(0), total: 0, last: null });
  const cb = useRef(onVote);
  cb.current = onVote;

  useEffect(() => {
    byUser.current = new Map();
    setTally({ counts: new Array(options).fill(0), total: 0, last: null });
    if (!active) return;
    return twitch.onMessage((m) => {
      const v = voteOf(m.text, options);
      if (v === null) return;
      if (byUser.current.get(m.userId) === v) return;
      byUser.current.set(m.userId, v);
      const counts = new Array(options).fill(0);
      for (const x of byUser.current.values()) counts[x - 1]++;
      setTally({ counts, total: byUser.current.size, last: m.name });
      cb.current?.();
    });
  }, [active, options]);

  return tally;
}

/// The option with the most votes (0-based), null on a tie or no votes.
export function leader(t: Tally): number | null {
  if (t.total === 0) return null;
  const max = Math.max(...t.counts);
  const top = t.counts.flatMap((c, i) => (c === max ? [i] : []));
  return top.length === 1 ? top[0] : null;
}
